import React, {FC, useCallback, useContext, useEffect, useRef, useState} from 'react'
import BimboShareConflictModal, {ConflictResponse} from './BimboShareConflictModal'
import BimboShareChangeBanner from './BimboShareChangeBanner'
import { BimboShareInactivityModal, BimboShareLockBanner, lockNotice } from './BimboShareLockNotice'
import useShareEditingTick, {claimShareLease, ShareLock, useWindowId} from "Hooks/useShareEditingTick";
import {arrivedSince, entryKey, ShareCollection, snapshotItems} from './BimboShareArrivals'
import { createPortal } from 'react-dom';
import Header from "../Layout/Header";
import {useTranslation} from "react-i18next";
import {Accordion, Button, Form} from "react-bootstrap";
import LoadingButton from "../Button/LoadingButton";
import FormEmpty, {FormResponseType} from "Vendor/Components/FormEmpty";
import {UserContext} from "Vendor/Context/UserContextProvider";
import useTranslationDataUtils from "Vendor/Utils/TranslationDataUtils";
import { classUtils as c } from 'Vendor/Utils/ClassUtils'
import Product from "Entity/Product";
import File from "Entity/File";
import Api from "Vendor/Api";
import ChooseItemsModal from "./ChooseItemsModal";
import useHumanizeFileSizeUtils from "Vendor/Utils/HumanizeFileSizeUtils";
import {BannerContext} from "../../Vendor/Context/BannerContextProvider";
import CheckboxType from "../../Vendor/Components/Form/Type/CheckboxType";
import Collection from "../../Config/Collection";
import { ExternalFiles } from 'Config/ExternalFiles';
import Property from "Entity/Property";
import Choice from "Entity/Property/Choice";
import BimboSharePreviewModal from "./BimboSharePreviewModal";
import BimboShareZipPreviewModal from "./BimboShareZipPreviewModal";
import BimboShareExternalPreviewModal from "./BimboShareExternalPreviewModal";
import {ExternalFileInput, FolderGrouping, FolderLevel, isImageName} from "./BimboShareFolders";
import {EditableProperty} from "Entity/Organization";

type BimboShareModalProps = {
    defaultCollection?: Collection
    defaultSelected?: number[]
    handleClose: (refresh?: boolean) => void
    currentBimboShare?: string
    setCurrentBimboShare?: (currentBimboShare?: string) => void
}

interface Items {
    buckets: {
        [key: string]: File[]
    }

    catalogs: {
        [key: string]: Product[]
    }
}

interface BimboShareAdditionalData {
    files: number[]
    products: number[]
    baseToken?: string
    overwrite?: boolean
    save?: boolean
    send?: boolean
    productWithPDF?: number
    productOriginalFile?: number
    productUniqueDirectory?: number
}

type FormAction = "save" | "send"

interface Preview {
    entity: 'bucket' | 'catalog'
    primarySlug: string
    index: number
}

interface EditResponse {
    uuid: string
    items: Items,
    externalFiles: ExternalFiles,
    formType: FormResponseType,
    productUniqueDirectory: boolean,
    productOriginalFile: boolean,
    productWithPDF: boolean,
    shareLink?: string,
    primaryProperty?: number,
    primaryPropertyChoices?: string[],
    secondaryProperty?: number,
    secondaryPropertyChoices?: string[],
    stateToken?: string,
}

const BimboShareModal: FC<BimboShareModalProps> = ({
                                                         handleClose,
                                                         currentBimboShare,
                                                         setCurrentBimboShare,
                                                         defaultCollection,
                                                         defaultSelected,
}) => {
    const { setBanner } = useContext(BannerContext);
    const bimboSharePath = `/bimbo-share/${currentBimboShare ?? 'new'}`
    const windowId = useWindowId()
    // Every read that reports the lease has to name the window asking, or this one is told its own lease
    // belongs to a stranger and the form goes read-only behind its own back.
    const bimboShareReadPath = `${bimboSharePath}?window=${windowId}`
    const {t, i18n} = useTranslation()
    const {currentUser} = useContext(UserContext)
    const {translate} = useTranslationDataUtils()
    const [formType, setFormType] = useState<boolean | FormResponseType>(!!currentBimboShare)
    const [externalFiles, setExternalFiles] = useState<ExternalFiles>({})
    const [items, setItems] = useState<Items|undefined>(() => {
        if (currentBimboShare || defaultCollection) {
            return undefined
        }

        let tempItems: Items = {
            buckets: {},
            catalogs: {},
        }

        currentUser?.currentOrganization?.buckets.forEach(bucket => {
            tempItems.buckets[bucket.primarySlug] = []

        })
        currentUser?.currentOrganization?.catalogs.forEach(catalog => {
            tempItems.catalogs[catalog.primarySlug] = []
        })

        return tempItems;
    })
    const [showModal, setShowModal] = useState<string|undefined>(undefined)
    const {humanFileSize} = useHumanizeFileSizeUtils()
    const [additionalData, setAdditionalData] = useState<BimboShareAdditionalData|undefined>(undefined)
    const [stateToken, setStateToken] = useState<string|undefined>(undefined)
    const [conflict, setConflict] = useState<ConflictResponse|undefined>(undefined)
    const [baselineItems, setBaselineItems] = useState<Items|undefined>(undefined)
    const [shareDeleted, setShareDeleted] = useState<boolean>(false)
    const [lock, setLock] = useState<ShareLock>('mine')
    const [lockLost, setLockLost] = useState<boolean>(false)
    const [changedWhileAway, setChangedWhileAway] = useState<boolean>(false)
    const heldLock = useRef<ShareLock|undefined>(undefined)
    const loadStarted = useRef<boolean>(false)
    const locked = 'mine' !== lock
    const [arrived, setArrived] = useState<Set<string>>(new Set())
    const [remoteToken, setRemoteToken] = useState<string|undefined>(undefined)
    const [dismissedToken, setDismissedToken] = useState<string|undefined>(undefined)
    const handleShowModal = useCallback((slug: string, show: boolean = false) => {
        if (show) {
            setShowModal(slug)
        } else if (showModal === slug) {
            setShowModal(undefined)
        }
    }, [showModal, setShowModal])
    const [formAction, setFormAction] = useState<FormAction>("save")
    const [onLoading, setOnLoading] = useState<boolean>(false)
    const [productWithPDF, setProductWithPDF] = useState<boolean>(false)
    const [productOriginalFile, setProductOriginalFile] = useState<boolean>(false)
    const [productUniqueDirectory, setProductUniqueDirectory] = useState<boolean>(false)
    const [success, setSuccess] = useState<boolean>(false)
    const [hasEdited, setHasEdited] = useState<boolean>(false)
    const [shareLink, setShareLink] = useState<string|undefined>(undefined);
    const inputFileRef = useRef(null);

    const [primaryProperty, setPrimaryProperty] = useState<number|undefined>(undefined);
    const [primaryPropertyChoices, setPrimaryPropertyChoices] = useState<string[]>([]);
    const [secondaryProperty, setSecondaryProperty] = useState<number|undefined>(undefined);
    const [secondaryPropertyChoices, setSecondaryPropertyChoices] = useState<string[]>([]);

    /**
     * Claims the lease before asking for the payload. Serialising a large share takes seconds, and a
     * lease claimed alongside it leaves the share reading as free for every one of them — long enough
     * for a second editor to walk in and take it.
     *
     * The ref is what keeps this to one run. React mounts an effect twice in development, and a second
     * run would reload a share the first is still fetching.
     */
    useEffect(() => {
        if (loadStarted.current) {
            return
        }

        loadStarted.current = true

        if (currentBimboShare) {
            claimShareLease(bimboSharePath, windowId)
                .then(answer => {
                    heldLock.current = answer
                    setLock(answer)
                })
                .then(() => Api.get(bimboShareReadPath))
                .then(({data}: {data: EditResponse}) => {
                    setStateToken(data.stateToken)
                    setBaselineItems(snapshotItems(data.items))
                    setItems(data.items)
                    setExternalFiles(data.externalFiles)
                    setFormType(data.formType)
                    setProductUniqueDirectory(data.productUniqueDirectory)
                    setProductOriginalFile(data.productOriginalFile)
                    setProductWithPDF(data.productWithPDF)
                    setShareLink(data?.shareLink || undefined)
                    if (data.primaryProperty) setPrimaryProperty(data.primaryProperty)
                    if (data.primaryPropertyChoices) setPrimaryPropertyChoices(data.primaryPropertyChoices)
                    if (data.secondaryProperty) setSecondaryProperty(data.secondaryProperty)
                    if (data.secondaryPropertyChoices) setSecondaryPropertyChoices(data.secondaryPropertyChoices)
                })
                .catch(() => {
                    setItems({ buckets: {}, catalogs: {} })
                    setBanner({
                        type: 'error',
                        i18nTitle: 'app.error',
                        i18nMessage: 'app.error_boundary_title',
                    })
                })

            return
        }

        if (defaultCollection) {
            Api.post('/bimbo-share/defaultItems', {
                defaultCollection,
                defaultSelected,
            })
            .then(({data}: {data: EditResponse}) => {
                setStateToken(data.stateToken)
                setItems(data.items)
                setProductUniqueDirectory(data.productUniqueDirectory)
                setProductOriginalFile(data.productOriginalFile)
                setProductWithPDF(data.productWithPDF)
            })
            .catch(() => {
                setItems({ buckets: {}, catalogs: {} })
                setBanner({
                    type: 'error',
                    i18nTitle: 'app.error',
                    i18nMessage: 'app.error_boundary_title',
                })
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
    const [preview, setPreview] = useState<Preview|undefined>(undefined)
    const [showPreview, setShowPreview] = useState<boolean>(false)
    const [showZipPreview, setShowZipPreview] = useState<boolean>(false)
    const [showExternalPreview, setShowExternalPreview] = useState<boolean>(false)
    const [externalStartIndex, setExternalStartIndex] = useState<number>(0)

    const cardDatasBySlug = React.useMemo(() => {
        const map: {[slug: string]: any[]} = {}
        currentUser?.currentOrganization?.buckets.forEach(bucket => {
            map[bucket.primarySlug] = bucket.listingSetting?.cardDatas ?? []
        })
        return map
    }, [currentUser])

    // Object URLs let us preview an image the user just picked, without a round trip. They are cached by blob
    // so a re-render (e.g. upload progress) reuses the same URL, and revoked once their blob leaves the list.
    const objectUrlsRef = useRef<Map<Blob, string>>(new Map())
    const externalFileInputs = React.useMemo<ExternalFileInput[]>(() => {
        const cache = objectUrlsRef.current
        const liveBlobs = new Set<Blob>()

        const inputs = Object.values(externalFiles).map(externalFile => {
            const name = externalFile.path.split('/').pop() ?? externalFile.path
            let previewUrl: string | undefined

            if (isImageName(name) && externalFile.data instanceof Blob) {
                liveBlobs.add(externalFile.data)
                previewUrl = cache.get(externalFile.data)
                if (!previewUrl) {
                    previewUrl = URL.createObjectURL(externalFile.data)
                    cache.set(externalFile.data, previewUrl)
                }
            }

            return {name, path: externalFile.path, isImage: isImageName(name), previewUrl}
        })

        cache.forEach((url, blob) => {
            if (!liveBlobs.has(blob)) {
                URL.revokeObjectURL(url)
                cache.delete(blob)
            }
        })

        return inputs
    }, [externalFiles])

    useEffect(() => () => {
        objectUrlsRef.current.forEach(url => URL.revokeObjectURL(url))
        objectUrlsRef.current.clear()
    }, [])

    useEffect(() => {
        if (shareDeleted) {
            setBanner({type: 'error', i18nTitle: 'app.error', i18nMessage: 'app.bimboShareForm.liveNotice.deleted'})
        }
    }, [shareDeleted, setBanner])

    useShareEditingTick({
        path: currentBimboShare && !shareDeleted ? bimboSharePath : undefined,
        window: windowId,
        token: stateToken,
        busy: onLoading || undefined !== conflict,
        dismissed: dismissedToken,
        onChanged: (token, afterIdle) => {
            setRemoteToken(token)

            if (afterIdle) {
                setChangedWhileAway(true)
            }
        },
        onDeleted: () => setShareDeleted(true),
        onLock: (answer: ShareLock) => {
            const notice = lockNotice(heldLock.current, answer)

            heldLock.current = answer
            setLock(answer)

            if (notice.lost) {
                setLockLost(true)
            }
        },
    })

    const hasItems = !!items && (
        Object.values(items.buckets).some(files => files.length > 0)
        || Object.values(items.catalogs).some(products => products.length > 0)
        || externalFileInputs.length > 0
    )

    const handleOpenPreview = (entity: Preview['entity'], primarySlug: string, index: number) => {
        setPreview({entity, primarySlug, index})
        setShowPreview(true)
    }

    const handleOpenExternalPreview = (index: number) => {
        setExternalStartIndex(index)
        setShowExternalPreview(true)
    }

    const previewCollection = preview && (preview.entity === 'bucket'
        ? currentUser?.currentOrganization?.buckets.find(bucket => bucket.primarySlug === preview.primarySlug)
        : currentUser?.currentOrganization?.catalogs.find(catalog => catalog.primarySlug === preview.primarySlug))
    const previewItems: (File | Product)[] = preview
        ? items?.[preview.entity === 'bucket' ? 'buckets' : 'catalogs'][preview.primarySlug] ?? []
        : []

    const previewGrouping: FolderGrouping = React.useMemo(() => {
        const editableProperties: EditableProperty[] = (currentUser?.currentOrganization as any)?.editable_properties
            || currentUser?.currentOrganization?.editableProperties
            || []

        const level = (propertyId: number | undefined, choices: string[] | null): FolderLevel | undefined => {
            if (!propertyId) {
                return undefined
            }

            const property = editableProperties.find(editableProperty => editableProperty.id === propertyId)

            return {slug: property?.slug, type: property?.type, choices: choices ?? []}
        }

        return {
            primary: level(primaryProperty, primaryPropertyChoices),
            secondary: level(secondaryProperty, secondaryPropertyChoices),
        }
    }, [currentUser, primaryProperty, primaryPropertyChoices, secondaryProperty, secondaryPropertyChoices])

    useEffect(() => {
        const updateTarget = () => {
            const el = document.getElementById('propertySelectsTarget');
            setTargetEl(prev => (prev !== el ? el : prev));
        };

        updateTarget();

        const modalEl = document.getElementById('bimboShareModal');
        if (!modalEl) return;

        const observer = new MutationObserver(() => {
            updateTarget();
        });

        observer.observe(modalEl, { childList: true, subtree: true });

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const initSelect2 = (id: string, onChange: (values: any) => void) => {
            const $el = $(`#${id}`);
            if ($el.length) {
                if ($el.data('select2')) {
                    $el.select2('destroy');
                }
                $el.select2({ theme: "bootstrap-5", width: "100%", dropdownParent: $("#bimboShareModal") });
                
                const borderClasses = ($el.attr("class") || "")
                    .split(/\s+/)
                    .filter((c: string) => c.startsWith("border"));
                if (borderClasses.length > 0) {
                    $el.next(".select2-container")
                        .find(".select2-selection")
                        .addClass(borderClasses.join(" "));
                }

                $el.off('change').on('change', (e: any) => {
                    onChange($(e.target).val());
                });
            }
        };

        // Small timeout to ensure React has rendered the select elements
        const timer = setTimeout(() => {
            initSelect2('bimbo_share_primaryProperty', (val: any) => {
                setPrimaryProperty(val ? parseInt(val as string, 10) : undefined);
                setPrimaryPropertyChoices([]);
            });
            initSelect2('bimbo_share_primaryPropertyChoices', setPrimaryPropertyChoices);
            initSelect2('bimbo_share_secondaryProperty', (val: any) => {
                setSecondaryProperty(val ? parseInt(val as string, 10) : undefined);
                setSecondaryPropertyChoices([]);
            });
            initSelect2('bimbo_share_secondaryPropertyChoices', setSecondaryPropertyChoices);
        }, 100);

        return () => clearTimeout(timer);
    });

    const handleSuccess = (data: EditResponse) => {
        setBanner({type: 'success', i18nTitle: 'app.save', i18nMessage: 'app.data_saved_message'})
        setStateToken(data.stateToken)
        // The server refuses a save from a window that does not hold the lease, so reaching here proves
        // it was ours.
        heldLock.current = 'mine'
        setLock('mine')
        setBaselineItems(snapshotItems(data.items))
        setItems(data.items)
        setArrived(new Set())
        setRemoteToken(undefined)
        setDismissedToken(undefined)
        let tempExternalFiles: ExternalFiles = {...externalFiles, ...data.externalFiles}
        setExternalFiles(tempExternalFiles)
        setFormType(data.formType)
        setSuccess(true)
        setCurrentBimboShare && setCurrentBimboShare(data.uuid)
        setHasEdited(true)
        setProductUniqueDirectory(data.productUniqueDirectory)
        setProductOriginalFile(data.productOriginalFile)
        setProductWithPDF(data.productWithPDF)
    }

    const handleReload = () => {
        setConflict(undefined)
        setRemoteToken(undefined)
        setDismissedToken(undefined)
        setLockLost(false)
        setChangedWhileAway(false)

        // A state refresh, not a page reload: reloading the page would drop any external file still
        // uploading, and close the window the user is working in. Claiming first is what hands the lease
        // back when the window that held it has since gone.
        claimShareLease(bimboSharePath, windowId)
            .then(answer => {
                heldLock.current = answer
                setLock(answer)
            })
            .then(() => Api.get(bimboShareReadPath))
            .then(({data}: {data: EditResponse}) => {
                setStateToken(data.stateToken)
                // Marked against the copy the window was built on, not the copy on screen, so a row the
                // user added themselves and lost to the reload is not reported as someone else's.
                setArrived(arrivedSince(baselineItems ?? data.items, data.items))
                setBaselineItems(snapshotItems(data.items))
                setItems(data.items)
                setExternalFiles(data.externalFiles)
                setFormType(data.formType)
                setProductUniqueDirectory(data.productUniqueDirectory)
                setProductOriginalFile(data.productOriginalFile)
                setProductWithPDF(data.productWithPDF)
                setShareLink(data?.shareLink || undefined)
                setPrimaryProperty(data.primaryProperty)
                setPrimaryPropertyChoices(data.primaryPropertyChoices ?? [])
                setSecondaryProperty(data.secondaryProperty)
                setSecondaryPropertyChoices(data.secondaryPropertyChoices ?? [])
            })
            .catch(() => {
                setBanner({
                    type: 'error',
                    i18nTitle: 'app.error',
                    i18nMessage: 'app.error_boundary_title',
                })
            })
    }

    const handleDismissNotice = () => {
        setDismissedToken(remoteToken)
        setRemoteToken(undefined)
    }

    const arrivalBadge = (collection: ShareCollection, slug: string, id?: number) => {
        if (undefined === id || !arrived.has(entryKey(collection, slug, id))) {
            return null
        }

        return (
            <span className="badge ms-2 bg-info">
                {t('app.bimboShareForm.arrivals.badge')}
            </span>
        )
    }

    const handleConflictOverwrite = () => {
        setConflict(undefined)
        setAdditionalData(previous => previous ? {...previous, overwrite: true} : previous)

        // Re-submitting picks the form fields back up; the next ordinary save rebuilds the payload
        // from scratch, so the override cannot outlive this one request.
        window.setTimeout(() => {
            const form = document.getElementById('bimboShareForm') as HTMLFormElement | null
            form?.requestSubmit()
        }, 0)
    }

    const handleOpenFileInput = () => {
        if (inputFileRef) {
            // @ts-ignore
            inputFileRef.current.click()
        }
    }

    const handleUploadExternalFiles = useCallback((files: any) => {
        let tempExternalFiles: ExternalFiles = {...externalFiles}
        for (let i = 0; i < files.length; i++) {
            tempExternalFiles[files[i].name] = {
                file_size: files[i].size,
                path: files[i].name,
                state: 'pending',
                data: files[i],
            }
        }
        setExternalFiles(tempExternalFiles)
    }, [externalFiles])

    const nameFilePath = (filePath: string) => {
        const segments = filePath.split('/')

        return segments[segments.length - 1]
    }

    useEffect(() => {
        if (currentBimboShare) {
            const pendingFiles = Object.entries(externalFiles).filter(
                ([, externalFile]) => externalFile.state === 'pending'
            )

            if (pendingFiles.length === 0) return;

            pendingFiles.forEach(([key, externalFile]) => {
                let formData = new FormData()
                formData.append('files[]', externalFile.data)
                setExternalFiles(prevExternalFiles => ({
                    ...prevExternalFiles,
                    [externalFile.path]: {
                        ...externalFile,
                        state: 'uploading',
                    }
                }))

                Api.post(`/bimbo-share/${currentBimboShare}/external-files/upload`, formData, {
                    baseURL: `https://s1-upload.${window.location.host.split(':')[0]}${process.env.REACT_APP_BACKEND_PORT ? ':' + process.env.REACT_APP_BACKEND_PORT: ''}`,
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    onUploadProgress: (progressEvent) => {
                        const { loaded, total } = progressEvent;
                        let percent = total ? Math.min(100, Math.floor((loaded * 100) / total)) : 0;

                        setExternalFiles(prevExternalFiles => ({
                            ...prevExternalFiles,
                            [externalFile.path]: {
                                ...prevExternalFiles[externalFile.path],
                                uploadProgress: percent,
                            }
                        }));
                    }
                }).then(({ data }) => {
                    setExternalFiles(prevExternalFiles => ({
                        ...prevExternalFiles,
                        [externalFile.path]: {
                            ...data.externalFiles[externalFile.path],
                            data: externalFile.data,
                            state: 'uploaded'
                        }
                    }));
                }).catch(() => {
                    setExternalFiles(prevExternalFiles => ({
                        ...prevExternalFiles,
                        [externalFile.path]: {
                            ...externalFile,
                            state: 'error',
                        }
                    }))
                })
            })
        }
    }, [currentBimboShare, externalFiles])

    const camelCase = (str: string) => str.replace(/-([a-z0-9])/g, g => g[1].toUpperCase());

    const combinedProperties = React.useMemo(() => {
        const propertiesMap = new Map<number, Property>();

        const editableProps = (currentUser?.currentOrganization as any)?.editable_properties 
                        || currentUser?.currentOrganization?.editableProperties;

        if (items && editableProps && Array.isArray(editableProps)) {
            const extractFromItems = (itemList: any[]) => {
                itemList.forEach(item => {
                    editableProps.forEach((ep: any) => {
                        if (!ep.slug || ep.id === undefined) return;
                        
                        const key = camelCase(ep.slug);
                        
                        if (item[key] !== undefined && item[key] !== null) {
                            let prop = propertiesMap.get(ep.id);
                            if (!prop) {
                                prop = {
                                    id: ep.id,
                                    name: ep.name,
                                    slug: ep.slug,
                                    type: ep.type as any,
                                    multiple: true,
                                    translate: false,
                                    expanded: false,
                                    phoneType: false,
                                    choices: [],
                                    deal: false,
                                    company: false,
                                    contact: false,
                                    dataCollections: [],
                                    catalogs: [],
                                    buckets: [],
                                } as unknown as Property;
                                propertiesMap.set(ep.id, prop);
                            }

                            const itemValues = Array.isArray(item[key]) ? item[key] : [item[key]];
                            itemValues.forEach((val: any) => {
                                if (val && val.id) {
                                    const exists = prop!.choices.find((c: Choice) => c.id === val.id);
                                    if (!exists) {
                                        prop!.choices.push(val as Choice);
                                    }
                                }
                            });
                        }
                    });
                });
            };

            if (items.buckets) {
                Object.values(items.buckets).forEach(bucketFiles => {
                    if (Array.isArray(bucketFiles)) extractFromItems(bucketFiles);
                });
            }

            if (items.catalogs) {
                Object.values(items.catalogs).forEach(catalogProducts => {
                    if (Array.isArray(catalogProducts)) extractFromItems(catalogProducts);
                });
            }
        }

        return Array.from(propertiesMap.values()).filter(p => p.type === 'text' || p.type === 'choice');
    }, [items, currentUser]);

    const renderPropertySelects = () => {
        if (!combinedProperties.length) return null;

        const primaryPropObj = combinedProperties.find(p => p.id === primaryProperty);
        const secondaryPropObj = combinedProperties.find(p => p.id === secondaryProperty);

        return (
            <>
                <Form.Group className="form-floating mb-3">
                    <Form.Select disabled={locked}
                        id="bimbo_share_primaryProperty" 
                        name="bimbo_share[primaryProperty]" 
                        className="border-primary form-select2"
                        value={primaryProperty ?? ''}
                        onChange={(e) => {
                            setPrimaryProperty(e.target.value ? parseInt(e.target.value, 10) : undefined);
                            setPrimaryPropertyChoices([]);
                        }}
                    >
                        <option value="">{i18n.language === 'en' ? 'Choose' : 'Choisir'}</option>
                        {combinedProperties.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </Form.Select>
                    <Form.Label>{i18n.language === 'en' ? 'Primary property' : 'Propriété principale'}</Form.Label>
                </Form.Group>

                {primaryPropObj && primaryPropObj.type === 'choice' && primaryPropObj.multiple && primaryPropObj.choices && primaryPropObj.choices.length > 0 && (
                    <Form.Group className="form-floating">
                        <Form.Select disabled={locked}
                            id="bimbo_share_primaryPropertyChoices" 
                            name="bimbo_share[primaryPropertyChoices][]" 
                            className="border-primary form-select2"
                            multiple
                            defaultValue={primaryPropertyChoices}
                        >
                            {primaryPropObj.choices.filter((c: Choice) => !c.deletedAt).map((c: any) => (
                                <option key={c.id} value={c.id?.toString()}>{c.translations?.[i18n.language]?.label || c.translations?.['fr']?.label || c.translations?.['en']?.label || c.label || c.value}</option>
                            ))}
                        </Form.Select>
                        <Form.Label>{i18n.language === 'en' ? 'Primary property choices' : 'Valeurs de la propriété principale'}</Form.Label>
                    </Form.Group>
                )}

                <Form.Group className="form-floating mb-3">
                    <Form.Select disabled={locked}
                        id="bimbo_share_secondaryProperty" 
                        name="bimbo_share[secondaryProperty]" 
                        className="border-primary form-select2"
                        value={secondaryProperty ?? ''}
                        onChange={(e) => {
                            setSecondaryProperty(e.target.value ? parseInt(e.target.value, 10) : undefined);
                            setSecondaryPropertyChoices([]);
                        }}
                    >
                        <option value="">{i18n.language === 'en' ? 'Choose' : 'Choisir'}</option>
                        {combinedProperties.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </Form.Select>
                    <Form.Label>{i18n.language === 'en' ? 'Secondary property' : 'Propriété secondaire'}</Form.Label>
                </Form.Group>

                {secondaryPropObj && secondaryPropObj.type === 'choice' && secondaryPropObj.multiple && secondaryPropObj.choices && secondaryPropObj.choices.length > 0 && (
                    <Form.Group className="form-floating">
                        <Form.Select disabled={locked}
                            id="bimbo_share_secondaryPropertyChoices" 
                            name="bimbo_share[secondaryPropertyChoices][]" 
                            className="border-primary form-select2"
                            multiple
                            defaultValue={secondaryPropertyChoices}
                        >
                            {secondaryPropObj.choices.filter((c: Choice) => !c.deletedAt).map((c: any) => (
                                <option key={c.id} value={c.id?.toString()}>{c.translations?.[i18n.language]?.label || c.translations?.['fr']?.label || c.translations?.['en']?.label || c.label || c.value}</option>
                            ))}
                        </Form.Select>
                        <Form.Label>{i18n.language === 'en' ? 'Secondary property choices' : 'Valeurs de la propriété secondaire'}</Form.Label>
                    </Form.Group>
                )}
            </>
        );
    };

    return (
        <div id="bimboShareModal">
            <Header
                externalUrl = {shareLink}
                externalUrlTitle={t('app.bimboShareForm.openShare')}
                previewButton={hasItems ? {
                    onClick: () => setShowZipPreview(true),
                    icon: 'fa-solid fa-folder-magnifying-glass',
                    title: t('app.bimboShareForm.previewZip'),
                } : undefined}
                title={t(currentBimboShare ? 'app.editBimboShare' : 'app.newBimboShare')}
                prevRotate={true}
                prevDisabled={onLoading}
                prev={() => {
                    handleClose(hasEdited)
                }}
            />
            <BimboShareLockBanner show={!lockLost} lock={lock} />
            <BimboShareInactivityModal
                show={lockLost || changedWhileAway}
                lock={lockLost ? lock : 'mine'}
                onReload={handleReload}
            />
            <BimboShareChangeBanner
                show={undefined !== remoteToken && undefined === conflict && !lockLost && !changedWhileAway}
                onReload={handleReload}
                onDismiss={handleDismissNotice}
            />
            <BimboShareConflictModal
                show={undefined !== conflict}
                reason={conflict?.reason}
                onReload={handleReload}
                onOverwrite={handleConflictOverwrite}
                onCancel={() => setConflict(undefined)}
            />
            {items && <BimboShareZipPreviewModal
                show={showZipPreview}
                handleClose={() => setShowZipPreview(false)}
                title={t('app.bimboShareForm.previewZip')}
                buckets={items.buckets}
                catalogs={items.catalogs}
                externalFiles={externalFileInputs}
                grouping={previewGrouping}
                productWithPDF={productWithPDF}
                productUniqueDirectory={productUniqueDirectory}
                cardDatasBySlug={cardDatasBySlug}
            />}
            <div className="content">
                <div className="selection">
                    <Accordion>
                        {currentUser?.currentOrganization?.buckets.map((bucket, key) =>
                        bucket.hidden ? null :
                            <>
                                <Accordion.Item eventKey={`bucket_${bucket.primarySlug}`}>
                                    <Accordion.Header>
                                        <span className="title">
                                            <i className={`fa-solid fa-${bucket.icon ?? 'file'}`}></i>
                                            {translate(bucket, 'name')}
                                            <span className={c('brand', !bucket.brand && 'brand-corporate')}>
                                               ({bucket.brand?.name ?? t('app.corporate')})
                                            </span>
                                        </span>
                                        <div>
                                            <span className="text-primary">{items !== undefined ? items?.buckets[bucket.primarySlug]?.length ?? 0 : <i className="fa-solid fa-circle-notch fa-spin disabled"></i>}</span>
                                            <i className={c("fa-solid text-primary", items !== undefined ? 'fa-plus' : 'fa-circle-notch fa-spin disabled')}
                                               title={t('app.add')}
                                               onClick={e => {
                                                   e.stopPropagation()
                                                   if (items !== undefined && !locked) {
                                                       handleShowModal(`bucket/${bucket.primarySlug}`, true)
                                                   }
                                               }}
                                            ></i>
                                        </div>
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        {items?.buckets[bucket.primarySlug]?.length ?
                                            <ul>
                                                {items?.buckets[bucket.primarySlug]?.map((file, key) =>
                                                    <li key={key}>
                                                        <button type="button" className="preview"
                                                            onClick={() => handleOpenPreview(
                                                                'bucket', bucket.primarySlug, key
                                                            )}
                                                        >
                                                            <span className="icon">
                                                                <img src={file.thumbnail?.replaceAll('__SIZING__', 'small')}  alt={file.name ?? file.id}/>
                                                            </span>
                                                            <span className="name">{file.name}</span>
                                                        </button>
                                                        <div>
                                                            {arrivalBadge('buckets', bucket.primarySlug, file.id)}
                                                            <span className="size">{file.__size ? humanFileSize(file.__size) : t('app.error')}</span>
                                                            <button className="remove" disabled={locked}
                                                                onClick={() => {
                                                                    let tempItems = {...items}
                                                                    tempItems.buckets[bucket.primarySlug] = tempItems.buckets[bucket.primarySlug].filter(comparedFile => comparedFile.id !== file.id)
                                                                    setItems(tempItems)
                                                                }}
                                                            >
                                                                <i className="fa-solid fa-x"></i>
                                                            </button>
                                                        </div>
                                                    </li>
                                                )}
                                            </ul>
                                            : <div className="text-center">{t('app.emptySelection')}</div>
                                        }
                                    </Accordion.Body>
                                </Accordion.Item>
                                {items && <ChooseItemsModal
                                    show={showModal === `bucket/${bucket.primarySlug}`}
                                    setShow={(show: boolean = false) => {
                                        handleShowModal(`bucket/${bucket.primarySlug}`, show)
                                    }}
                                    selected={items.buckets[bucket.primarySlug] ?? []}
                                    setSelected={(selected) => {
                                        let tempItems = {...items}
                                        tempItems.buckets[bucket.primarySlug] = selected as File[]

                                        setItems(tempItems)
                                    }}
                                    primarySlug={bucket.primarySlug}
                                    title={translate(bucket, 'name')}
                                    path={`/b/${bucket.primarySlug}`}
                                    entity="bucket"
                                    multiple={true}
                                    selectionNamespace="bimboshare"
                                />}
                            </>
                        )}
                        {currentUser?.currentOrganization?.catalogs.map((catalog, key) =>
                            <>
                                <Accordion.Item eventKey={`catalog_${catalog.primarySlug}`}>
                                    <Accordion.Header>
                                        <span className="title">
                                            <i className="fa-solid fa-bag-shopping"></i>
                                            {translate(catalog, 'name')}
                                            <span className={c('brand', !catalog.brand && 'brand-corporate')}>
                                               ({catalog.brand?.name ?? t('app.corporate')})
                                            </span>
                                        </span>
                                        <div>
                                            <span className="text-primary">{items !== undefined ? items?.catalogs[catalog.primarySlug]?.length ?? 0 : <i className="fa-solid fa-circle-notch fa-spin disabled"></i>}</span>
                                            <i className={c("fa-solid  text-primary", items !== undefined ? 'fa-plus' : 'fa-circle-notch fa-spin disabled')}
                                               title={t('app.add')}
                                               onClick={e => {
                                                   e.stopPropagation()
                                                   if (items !== undefined && !locked) {
                                                       handleShowModal(`catalog/${catalog.primarySlug}`, true)
                                                   }
                                               }}
                                            ></i>
                                        </div>
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        {items?.catalogs[catalog.primarySlug]?.length ?
                                            <>
                                                <div key={0}>
                                                    <CheckboxType id="productWithPDF" name="productWithPDF" fullName="productWithPDF" required={false} checked={productWithPDF} setValue={setProductWithPDF} checkedByDefault={false} i18nLabel="app.bimboShareForm.productWithPDF" />
                                                    <CheckboxType id="productOriginalFile" name="productOriginalFile" fullName="productOriginalFile" required={false} checked={productOriginalFile} setValue={setProductOriginalFile} checkedByDefault={false} i18nLabel="app.bimboShareForm.productOriginalFile" />
                                                    <CheckboxType id="productUniqueDirectory" name="productUniqueDirectory" fullName="productUniqueDirectory" required={false} checked={productUniqueDirectory} setValue={setProductUniqueDirectory} checkedByDefault={false} i18nLabel="app.bimboShareForm.productUniqueDirectory" />
                                                </div>
                                                <ul key={1}>
                                                    {items?.catalogs[catalog.primarySlug]?.map((product, key) =>
                                                        <li key={key}>
                                                            <button type="button" className="preview"
                                                                onClick={() => handleOpenPreview(
                                                                    'catalog', catalog.primarySlug, key
                                                                )}
                                                            >
                                                                <span className="icon">
                                                                    <img src={product.thumbnail?.replaceAll('__SIZING__', 'small')}  alt={product.name ?? product.id}/>
                                                                </span>
                                                                <span className="name">{translate(product, 'name')}</span>
                                                            </button>
                                                            <div>
                                                                {arrivalBadge(
                                                                    'catalogs',
                                                                    catalog.primarySlug,
                                                                    product.id,
                                                                )}
                                                                <button className="remove" disabled={locked}
                                                                        onClick={() => {
                                                                            let tempItems = {...items}
                                                                            tempItems.catalogs[catalog.primarySlug] = tempItems.catalogs[catalog.primarySlug].filter(comparedProduct => comparedProduct.id !== product.id)
                                                                            setItems(tempItems)
                                                                        }}
                                                                >
                                                                    <i className="fa-solid fa-x"></i>
                                                                </button>
                                                            </div>
                                                        </li>
                                                    )}
                                                </ul>
                                            </>
                                            : <div className="text-center">{t('app.emptySelection')}</div>
                                        }
                                    </Accordion.Body>
                                </Accordion.Item>
                                {items && <ChooseItemsModal
                                    show={showModal === `catalog/${catalog.primarySlug}`}
                                    setShow={(show: boolean = false) => {
                                        handleShowModal(`catalog/${catalog.primarySlug}`, show)
                                    }}
                                    selected={items.catalogs[catalog.primarySlug] ?? []}
                                    setSelected={(selected) => {
                                        let tempItems = {...items}
                                        tempItems.catalogs[catalog.primarySlug] = selected as Product[]
                                        setItems(tempItems)
                                    }}
                                    primarySlug={catalog.primarySlug}
                                    title={translate(catalog, 'name')}
                                    path={`/c/${catalog.primarySlug}`}
                                    entity="catalog"
                                    multiple={true}
                                    selectionNamespace="bimboshare"
                                />}
                            </>
                        )}
                        <Accordion.Item eventKey={`externalFiles`}>
                            <Accordion.Header>
                                <span className="title">
                                    <i className="fa-solid fa-file-import"></i>
                                    {t('app.external_files.label')}
                                </span>
                                <div>
                                    <span className="text-primary">{Object.values(externalFiles).length ?? 0}</span>
                                    <label className="fa-solid fa-plus text-primary"
                                        title={t('app.external_files.add')}
                                        onClick={e => {
                                            e.stopPropagation()
                                        }}
                                    >
                                        <input ref={inputFileRef} type="file" id="external-files-input" className="d-none" multiple onChange={(e) => {
                                            if (e.target.files && e.target.files.length) {
                                                e.stopPropagation()
                                                handleUploadExternalFiles(e.target.files)
                                            }
                                        }}/>
                                    </label>
                                </div>
                            </Accordion.Header>
                            <Accordion.Body>
                                {Object.values(externalFiles).length ?
                                    <ul>
                                        {Object.values(externalFiles).map((externalFile, key) =>
                                            <li key={key}>
                                                <div>
                                                    <button type="button" className="preview"
                                                        onClick={() => handleOpenExternalPreview(key)}>
                                                        <span className="name">{nameFilePath(externalFile.path)}</span>
                                                    </button>
                                                    <span className="ms-2">- {t('app.external_files.state.' + externalFile.state)}{externalFile.uploadProgress && externalFile.uploadProgress && <span>{' - ' + externalFile.uploadProgress + ' %'}{100 === externalFile.uploadProgress && <>{t('app.pending_validation')}</>}</span>}</span>
                                                </div>
                                                <div>
                                                    <span className="size">{humanFileSize(externalFile.file_size)}</span>
                                                    <button className="remove" disabled={locked}
                                                        onClick={() => {
                                                            if (currentBimboShare && externalFile.state === 'uploaded') {
                                                                Api.delete(`/bimbo-share/${currentBimboShare}/external-files/remove`, {
                                                                    data: {
                                                                        'filePath': externalFile.path,
                                                                    }
                                                                }).then(() => {
                                                                    let tempExternalFiles: ExternalFiles = {...externalFiles}
                                                                    delete tempExternalFiles[externalFile.path.split('/').reverse()[0]]
                                                                    setExternalFiles(tempExternalFiles)
                                                                }).catch(() => {
                                                                    setBanner({
                                                                        type: 'error',
                                                                        i18nTitle: 'app.error',
                                                                        i18nMessage: 'app.error_boundary_title',
                                                                    })
                                                                })
                                                            } else {
                                                                let tempExternalFiles: ExternalFiles = {...externalFiles}
                                                                delete tempExternalFiles[externalFile.path.split('/').reverse()[0]]
                                                                setExternalFiles(tempExternalFiles)
                                                            }
                                                        }}
                                                    >
                                                        <i className="fa-solid fa-x"></i>
                                                    </button>
                                                </div>
                                            </li>
                                        )}
                                    </ul>
                                    : <div className="text-center">{t('app.emptySelection')}</div>
                                }
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                    {preview && previewCollection && <BimboSharePreviewModal
                        show={showPreview}
                        handleClose={() => setShowPreview(false)}
                        title={translate(previewCollection, 'name')}
                        entity={preview.entity}
                        items={previewItems}
                        cardDatas={previewCollection.listingSetting?.cardDatas ?? []}
                        grouping={previewGrouping}
                        startIndex={preview.index}
                    />}
                    {showExternalPreview && <BimboShareExternalPreviewModal
                        show={showExternalPreview}
                        handleClose={() => setShowExternalPreview(false)}
                        title={t('app.external_files.label')}
                        files={externalFileInputs}
                        startIndex={externalStartIndex}
                    />}
                </div>

                <div className={c('form', locked && 'bimbo-share-locked')}>
                    {(() => {
                        const propertySelectsContent = renderPropertySelects();
                        return (
                            <>
                                <FormEmpty
                                    formContent={{className: 'form-content'}}
                                    formSubmit={{className: "form-actions"}}
                                    method={currentBimboShare ? 'PATCH' : 'POST'}
                                    path={bimboSharePath}
                                    // The save has to name the window too: the lease belongs to a window,
                                    // so a nameless save cannot be told apart from a second one's.
                                    additionalParams={{window: windowId}}
                                    formId="bimboShareForm"
                                    onConflict={(data: ConflictResponse) => setConflict(data)}
                                    delocateInitForm={formType}
                                    additionalData={additionalData}
                                    appendForm={!targetEl ? propertySelectsContent : undefined}
                                    onSubmit={() => {
                                        setOnLoading(true)
                                    }}
                                    onResponse={() => {
                                        setOnLoading(false)
                                    }}
                                    onSuccess={handleSuccess}
                                >
                                    <Button variant="secondary" disabled={onLoading} onClick={() => {
                                        handleClose(hasEdited)
                                    }}>{t(currentBimboShare ? 'app.close' : 'app.cancel')}</Button>
                                    <LoadingButton
                                        type="submit"
                                        variant="outline-primary"
                                        isLoading={onLoading && formAction === "save"}
                                        disabled={onLoading || locked}
                                        success={success && formAction === "save"}
                                        setSuccess={setSuccess}
                                        onClick={(e) => {
                                            let tempAdditionalData: BimboShareAdditionalData = {
                                                files: [],
                                                products: [],
                                                send: false,
                                                baseToken: stateToken,
                                            }

                                            if (items?.buckets) {
                                                for (const [index, bucket] of Object.entries(items.buckets)) {
                                                    bucket.forEach(file => {
                                                        if (file.id) {
                                                            tempAdditionalData.files.push(file.id)
                                                        }
                                                    })
                                                }
                                            }

                                            if (items?.catalogs) {
                                                for (const [index, catalog] of Object.entries(items.catalogs)) {
                                                    catalog.forEach(product => {
                                                        if (product.id) {
                                                            tempAdditionalData.products.push(product.id)
                                                        }
                                                    })
                                                }

                                                if (tempAdditionalData.products.length) {
                                                    tempAdditionalData.productWithPDF = productWithPDF ? 1 : undefined
                                                    tempAdditionalData.productOriginalFile = productOriginalFile ? 1 : undefined
                                                    tempAdditionalData.productUniqueDirectory = productUniqueDirectory ? 1 : undefined
                                                } else {
                                                    tempAdditionalData.productWithPDF = undefined
                                                    tempAdditionalData.productOriginalFile = undefined
                                                    tempAdditionalData.productUniqueDirectory = undefined
                                                }
                                            }

                                            tempAdditionalData.send = false;
                                            setAdditionalData(tempAdditionalData)
                                            setFormAction("save")
                                        }}
                                    >
                                        {t('app.save')}
                                        <i className="fa-solid fa-floppy-disk ms-2"></i>
                                    </LoadingButton>
                                    <LoadingButton
                                        type="submit"
                                        variant="primary"
                                        isLoading={onLoading && formAction === "send"}
                                        success={success && formAction === "send"}
                                        setSuccess={setSuccess}
                                        disabled={onLoading || locked || !currentBimboShare}
                                        onClick={(e) => {
                                            let tempAdditionalData: BimboShareAdditionalData = {
                                                files: [],
                                                products: [],
                                                send: false,
                                                baseToken: stateToken,
                                            }

                                            if (items?.buckets) {
                                                for (const [index, bucket] of Object.entries(items.buckets)) {
                                                    bucket.forEach(file => {
                                                        if (file.id) {
                                                            tempAdditionalData.files.push(file.id)
                                                        }
                                                    })
                                                }
                                            }

                                            if (items?.catalogs) {
                                                for (const [index, catalog] of Object.entries(items.catalogs)) {
                                                    catalog.forEach(product => {
                                                        if (product.id) {
                                                            tempAdditionalData.products.push(product.id)
                                                        }
                                                    })
                                                }

                                                if (tempAdditionalData.products.length) {
                                                    tempAdditionalData.productWithPDF = productWithPDF ? 1 : undefined
                                                    tempAdditionalData.productOriginalFile = productOriginalFile ? 1 : undefined
                                                    tempAdditionalData.productUniqueDirectory = productUniqueDirectory ? 1 : undefined
                                                } else {
                                                    tempAdditionalData.productWithPDF = undefined
                                                    tempAdditionalData.productOriginalFile = undefined
                                                    tempAdditionalData.productUniqueDirectory = undefined
                                                }
                                            }

                                            tempAdditionalData.send = true;
                                            setAdditionalData(tempAdditionalData)
                                            setFormAction("send")
                                        }}
                                    >
                                        {t('app.send')}
                                        <i className="fa-solid fa-paper-plane ms-2"></i>
                                    </LoadingButton>
                                </FormEmpty>
                                {targetEl && propertySelectsContent ? createPortal(propertySelectsContent, targetEl) : null}
                            </>
                        );
                    })()}
                </div>
            </div>
        </div>
    )
}

export default BimboShareModal
